import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Flower2, Sun, Leaf, Snowflake } from "lucide-react";
import { useVerse } from "@/lib/bible";

const IMG = "https://static.prod-images.emergentagent.com/jobs/2bfd65d8-4762-4154-9373-440077215f5a/images/";

export const SEASONS = [
  { id: "spring", label: "Spring", theme: "New life", icon: Flower2, img: IMG + "481d583235d1f3d0ffcd0989289b9d2c7fbc6e4b46f463d0f8d5cc11a19aa357.jpeg", tint: "from-[#2b3a4a]/80", particle: "petal",
    verse: "The flowers appear on the earth; the time of the singing of birds is come.", ref: "Song of Solomon 2:12" },
  { id: "summer", label: "Summer", theme: "Light & abundance", icon: Sun, img: IMG + "91e01a8a44b6112481ea12d0b55af158178edc555a3ed570558a87273cb2c374.jpeg", tint: "from-[#1E293B]/80", particle: "mote",
    verse: "The heavens declare the glory of God; and the firmament sheweth his handywork.", ref: "Psalm 19:1" },
  { id: "autumn", label: "Autumn", theme: "Harvest & gratitude", icon: Leaf, img: IMG + "d99542863fef4d614aff61b69d11eca862840b485ada55b7d0c2346e9cc51f25.jpeg", tint: "from-[#2a1c17]/85", particle: "leaf",
    verse: "And let us not be weary in well doing: for in due season we shall reap, if we faint not.", ref: "Galatians 6:9" },
  { id: "winter", label: "Winter", theme: "Hope in the dark", icon: Snowflake, img: IMG + "332c370e30b251cba72c545816f715d8aa4d6db7a9c8a48f32d3ebacb6a67277.jpeg", tint: "from-[#0b1222]/85", particle: "snow",
    verse: "The people that walked in darkness have seen a great light.", ref: "Isaiah 9:2" },
];

export const currentSeasonIndex = () => [3, 3, 0, 0, 0, 1, 1, 1, 2, 2, 2, 3][new Date().getMonth()];

function Particles({ type }: { type: string }) {
  const items = useMemo(() => Array.from({ length: type === "snow" ? 46 : 26 }, (_, i) => ({
    left: Math.random() * 100, delay: -Math.random() * 14, dur: (type === "mote" ? 9 : 11) + Math.random() * 9, size: 0.6 + Math.random() * 0.9, sway: 20 + Math.random() * 60, key: i,
  })), [type]);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {items.map((p) => (
        <span key={p.key} className={`particle particle-${type}`}
          style={{ left: `${p.left}%`, animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s`, ["--s" as any]: p.size, ["--sway" as any]: `${p.sway}px` }} />
      ))}
    </div>
  );
}

// Water regions & boat cut-outs in image % coords so ripples stay on the water only
const WATER: Record<string, { water: string; boat: string; fish: [number, number][] }> = {
  winter: { water: "M34 52 L100 50 L100 90 L62 92 L42 88 L32 79 L26 67 L32 59 Z", boat: "M52 40 L77 40 L77 74 L52 74 Z", fish: [[48, 80], [86, 62], [40, 70]] },
  spring: { water: "M0 39 L32 41 L42 47 L55 52 L64 57 L70 64 L74 70 L70 78 L60 86 L40 90 L20 92 L0 92 Z", boat: "M57 25 L60 25 L67 63 L72 66 L71 79 L65 80 L46 76 L42 62 L45 55 L51 47 Z", fish: [[12, 55], [30, 66], [18, 62]] },
  summer: { water: "M0 38 L50 40 L55 42 L64 45 L64 54 L79 55 L72 58 L60 61 L48 65 L22 68 L20 55 L0 50 Z", boat: "M24 31 L44 31 L44 63 L24 63 Z", fish: [[9, 44], [52, 50], [62, 44]] },
  autumn: { water: "M40 57 L64 56 L76 51 L88 51 L88 57 L76 60 L76 79 L68 80 L68 86 L46 88 L44 64 Z", boat: "M35 62 L68 62 L68 89 L35 89 Z", fish: [[76, 55], [56, 52], [88, 58]] },
};

const maskFor = (w: (typeof WATER)[string]) => {
  // white water minus black boat, eroded by a black stroke so ripples stay inside the shoreline
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' preserveAspectRatio='none'><defs><filter id='b'><feGaussianBlur stdDeviation='0.6'/></filter><mask id='m'><g filter='url(#b)'><path d='${w.water}' fill='white' stroke='black' stroke-width='3'/><path d='${w.boat}' fill='black' stroke='black' stroke-width='2'/></g></mask></defs><rect width='100' height='100' fill='white' mask='url(#m)'/></svg>`;
  const url = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
  return { WebkitMaskImage: url, maskImage: url, WebkitMaskSize: "100% 100%", maskSize: "100% 100%" } as React.CSSProperties;
};

function JumpingFish({ spots, tone }: { spots: [number, number][]; tone: string }) {
  const [jump, setJump] = useState<{ k: number; x: number; y: number; dir: number } | null>(null);
  useEffect(() => {
    let t: any;
    const next = () => {
      t = setTimeout(() => {
        const [x, y] = spots[Math.floor(Math.random() * spots.length)];
        setJump({ k: Date.now(), x, y, dir: Math.random() > 0.5 ? 1 : -1 });
        next();
      }, 3500 + Math.random() * 4500);
    };
    t = setTimeout(() => { setJump({ k: Date.now(), x: spots[0][0], y: spots[0][1], dir: 1 }); next(); }, 1800);
    return () => clearTimeout(t);
  }, [spots]);
  if (!jump) return null;
  return (
    <div key={jump.k} className="pointer-events-none absolute" style={{ left: `${jump.x}%`, top: `${jump.y}%`, width: "4.5%" }} data-testid="jumping-fish">
      <span className="fish-splash" style={{ animationDelay: "0s" }} />
      <span className="fish-splash" style={{ animationDelay: "1.05s", left: `${jump.dir * 140}%` }} />
      <motion.svg viewBox="0 0 64 32" className="absolute left-0 top-0 w-full overflow-visible" style={{ scaleX: jump.dir }}
        initial={{ x: "0%", y: "0%", rotate: -55, opacity: 0 }}
        animate={{ x: ["0%", `${jump.dir * 70}%`, `${jump.dir * 140}%`], y: ["0%", "-260%", "0%"], rotate: [-55 * jump.dir, 0, 60 * jump.dir], opacity: [0, 1, 1, 0] }}
        transition={{ duration: 1.1, ease: "easeInOut", times: [0, 0.5, 1] }}>
        <path d="M4 16 C14 4 36 2 50 14 L62 6 L58 16 L62 26 L50 18 C36 30 14 28 4 16 Z" fill={tone} />
        <circle cx="14" cy="14" r="2" fill="#fff" opacity="0.8" />
      </motion.svg>
    </div>
  );
}

function Scene({ s, compact }: { s: (typeof SEASONS)[number]; compact?: boolean }) {
  const w = WATER[s.id];
  const mask = useMemo(() => maskFor(w), [w]);
  return (
    <motion.div key={s.id} className="absolute inset-0 overflow-hidden [container-type:size]" initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.6, ease: "easeOut" }}>
      <div className="hero-drift absolute left-1/2 top-1/2" style={{ width: "max(100cqw, 149.06cqh)", aspectRatio: "1264 / 848", translate: "-50% -50%" }} data-testid="hero-image-frame">
        <img src={s.img} alt={`${s.label} on the Sea of Galilee with a fishing boat`} className="absolute inset-0 h-full w-full" />
        {s.id !== "winter" && <>
          <img src={s.img} alt="" aria-hidden className="hero-water absolute inset-0 h-full w-full" style={mask} data-testid="hero-water-layer" />
          <div className="hero-shimmer absolute inset-0" style={mask} />
        </>}
        {s.id === "winter" && <div className="hero-star" />}
        <JumpingFish spots={w.fish} tone={s.id === "winter" ? "#CBD5E1" : "#3B4A54"} />
      </div>
      <div className="hero-cloud hero-cloud-a" /><div className="hero-cloud hero-cloud-b" />
      {s.id === "summer" && <div className="hero-rays" />}
      <Particles type={s.particle} />
      <div className={`absolute inset-0 bg-gradient-to-r ${s.tint} via-transparent to-transparent`} />
      {compact ? <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-slate-950/70 to-transparent" />
        : <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#FAF7F2] to-transparent" />}
    </motion.div>
  );
}

export function SeasonalHero({ children, compact }: { children: React.ReactNode; compact?: boolean }) {
  const [idx, setIdx] = useState(currentSeasonIndex);
  const s = SEASONS[idx];
  const verse = useVerse(s.ref, s.verse);

  const turb = useRef<SVGFETurbulenceElement>(null);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    const tick = (t: number) => {
      const k = (1 - Math.cos((t / 16000) * Math.PI * 2)) / 2;
      turb.current?.setAttribute("baseFrequency", `${(0.006 + 0.002 * k).toFixed(5)} ${(0.06 + 0.015 * k).toFixed(5)}`);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <section className={`relative overflow-hidden bg-[#1E293B] ${compact ? "min-h-[520px] rounded-[2rem] shadow-2xl shadow-slate-900/15" : "min-h-[92vh]"}`} data-testid="seasonal-hero" data-season={s.id}>
      <svg width="0" height="0" className="absolute">
        <filter id="cast-water">
          <feTurbulence ref={turb} type="fractalNoise" baseFrequency="0.006 0.06" numOctaves="2" seed="3" />
          <feDisplacementMap in="SourceGraphic" scale="5" />
        </filter>
      </svg>
      <AnimatePresence mode="sync"><Scene key={s.id} s={s} compact={compact} /></AnimatePresence>

      {compact ? (
        <div className="relative z-10 flex min-h-[520px] flex-col justify-end p-6 pb-24 sm:p-10 sm:pb-24">{children}</div>
      ) : (
      <div className="relative z-10 mx-auto flex min-h-[92vh] max-w-7xl flex-col justify-center px-5 pb-40 pt-16 sm:px-8">
        {children}
        <AnimatePresence mode="wait">
          <motion.figure key={s.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.8 }}
            className="mt-12 max-w-md rounded-2xl border border-white/20 bg-white/10 p-5 text-amber-50 backdrop-blur-md" data-testid="season-verse">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-200">{s.label} · {s.theme}</p>
            <blockquote className="mt-2 font-scripture text-xl italic leading-relaxed">“{verse.text}”</blockquote>
            <figcaption className="mt-2 text-sm font-semibold text-amber-200">{s.ref} · {verse.abbr}</figcaption>
          </motion.figure>
        </AnimatePresence>
      </div>
      )}

      {!compact && <div className="absolute bottom-16 right-5 z-20 flex gap-2 sm:bottom-12 sm:right-8" data-testid="season-switcher">
        {SEASONS.map((x, i) => (
          <button key={x.id} data-testid={`season-button-${x.id}`} onClick={() => setIdx(i)} aria-pressed={i === idx} aria-label={x.label}
            className={`relative flex items-center gap-1.5 overflow-hidden rounded-full px-3.5 py-2 text-sm font-semibold shadow-lg backdrop-blur-md transition-all ${i === idx ? "bg-white text-slate-900" : "bg-slate-900/80 text-white ring-1 ring-white/30 hover:bg-slate-900"}`}>
            <x.icon className="h-4 w-4" /><span className="hidden sm:inline">{x.label}</span>
          </button>
        ))}
      </div>}
      {compact && <div className="absolute bottom-6 right-6 z-20 flex items-center gap-1.5 rounded-full bg-white px-3.5 py-2 text-sm font-semibold text-slate-900 shadow-lg" data-testid="season-current">
        <s.icon className="h-4 w-4" />{s.label}
      </div>}
    </section>
  );
}
